import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  type DocumentTypeListItem,
  fetchDocumentTypesList,
} from '@/api/documents';

// The `/document-types` route caps `count` server-side. Requesting the cap
// keeps the common case to a single round trip while `fetchAllDocumentTypes`
// still walks `pagination.totalPages`, so growing past the cap cannot silently
// truncate the list again (DELTA-2421: a hardcoded page-0-only fetch hid 49 of
// 149 company types, including every one from "P…" onwards).
const PAGE_SIZE = 500;

export interface DocumentTypesQueryParams {
  documentClass?: 'TRANSACTIONAL' | 'COMPANY' | 'ALL';
}

/**
 * Fetches every non-deprecated document type, walking all pages.
 *
 * Deprecated types are excluded server-side, so the result is always the
 * currently selectable set.
 */
export async function fetchAllDocumentTypes(
  params: DocumentTypesQueryParams = {},
): Promise<DocumentTypeListItem[]> {
  const qs = (page: number): string =>
    new URLSearchParams({
      ...(params.documentClass ? { documentClass: params.documentClass } : {}),
      page: String(page),
      count: String(PAGE_SIZE),
    }).toString();

  const first = await fetchDocumentTypesList(qs(0));
  const items: DocumentTypeListItem[] = [...first.data];
  const pageCount = first.metadata.pagination.totalPages;
  for (let page = 1; page < pageCount; page++) {
    const res = await fetchDocumentTypesList(qs(page));
    items.push(...res.data);
  }
  return items;
}

export const documentTypeKeys = {
  all: (params: DocumentTypesQueryParams = {}) =>
    ['document-types', 'all', params.documentClass ?? 'ALL'] as const,
};

/** Usable in route loaders via `queryClient.ensureQueryData`. */
export function allDocumentTypesQueryOptions(
  params: DocumentTypesQueryParams = {},
) {
  return queryOptions({
    queryKey: documentTypeKeys.all(params),
    queryFn: () => fetchAllDocumentTypes(params),
    staleTime: 5 * 60_000,
  });
}

export function useAllDocumentTypes(params: DocumentTypesQueryParams = {}) {
  return useQuery(allDocumentTypesQueryOptions(params));
}
